import { createContext, useContext } from 'react'

/**
 * The local-camera context object and its hook, apart from the provider.
 *
 * The provider (MediaStreamContext.tsx) imports the meeting session store, so
 * any edit there re-ran that module under hot reload and `createContext`
 * minted a second context: tiles read the new one while the provider on
 * screen still held the old, and the meeting died with "useMediaStream must be
 * used within MediaStreamProvider". This module imports nothing that changes,
 * so there is only ever one. Same reason as the tour's `tour-context`.
 */
export interface MediaStreamContextValue {
  getLocalStream: () => Promise<MediaStream>
  releaseLocalStream: () => void
  getStreamRefCount: () => number
  /**
   * Hear about the camera being replaced under a stream already handed out —
   * a device switch, or a dead camera the manager reopened. Returns an
   * unsubscribe. `null` is the camera closing.
   */
  subscribeLocalStream: (listener: (stream: MediaStream | null) => void) => () => void
}

export const MediaStreamContext = createContext<MediaStreamContextValue | null>(null)

export function useMediaStream() {
  const context = useContext(MediaStreamContext)
  if (!context) {
    throw new Error('useMediaStream must be used within MediaStreamProvider')
  }
  return context
}
